/**
 * RoutePhotosGallery — grid de miniaturas com lightbox modal simples.
 *
 * Props:
 *   routeId    — string
 *   count      — número de fotos disponíveis (do backend)
 *   apiUrl     — API base (para construir URLs)
 *   canDelete  — se true, mostra botão "×" para apagar
 *   onDelete   — callback (idx) → Promise. Chamado ao clicar ×.
 */
import React, { useState, useCallback, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';

const RoutePhotosGallery = ({ routeId, count, apiUrl, canDelete = false, onDelete }) => {
  const [lightbox, setLightbox] = useState(-1); // -1 = fechado
  const items = Array.from({ length: count || 0 }, (_, i) => i);

  const prev = useCallback(() => setLightbox((i) => (i > 0 ? i - 1 : items.length - 1)), [items.length]);
  const next = useCallback(() => setLightbox((i) => (i < items.length - 1 ? i + 1 : 0)), [items.length]);

  // Navegação por teclado no lightbox
  useEffect(() => {
    if (lightbox < 0) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightbox(-1);
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, prev, next]);

  if (!count) {
    return <p className="text-sm text-muted-foreground">Sem fotos ainda.</p>;
  }

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
        {items.map((idx) => (
          <div
            key={idx}
            className="relative group aspect-video rounded-lg overflow-hidden bg-muted cursor-pointer"
            onClick={() => setLightbox(idx)}
            data-testid={`route-photo-${idx}`}
          >
            <img
              src={`${apiUrl}/routes/${routeId}/photos/${idx}`}
              alt={`Foto ${idx + 1}`}
              className="w-full h-full object-cover transition-transform group-hover:scale-105"
              loading="lazy"
              onError={(e) => { e.target.parentElement.style.display = 'none'; }}
            />
            {canDelete && (
              <button
                type="button"
                className="absolute top-1 right-1 h-6 w-6 rounded-full bg-red-600/90 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => { e.stopPropagation(); onDelete && onDelete(idx); }}
                data-testid={`route-photo-delete-${idx}`}
                aria-label="Remover foto"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Lightbox modal */}
      {lightbox >= 0 && (
        <div
          className="fixed inset-0 z-[9999] bg-black/85 flex items-center justify-center p-4"
          onClick={() => setLightbox(-1)}
          data-testid="photo-lightbox"
        >
          <button
            type="button"
            className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            onClick={(e) => { e.stopPropagation(); setLightbox(-1); }}
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>
          {items.length > 1 && (
            <>
              <button
                type="button"
                className="absolute left-4 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                onClick={(e) => { e.stopPropagation(); prev(); }}
                aria-label="Anterior"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                type="button"
                className="absolute right-4 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                onClick={(e) => { e.stopPropagation(); next(); }}
                aria-label="Seguinte"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          )}
          <img
            src={`${apiUrl}/routes/${routeId}/photos/${lightbox}`}
            alt={`Foto ${lightbox + 1}`}
            className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/40 px-3 py-1 rounded-full">
            {lightbox + 1} / {items.length}
          </div>
        </div>
      )}
    </>
  );
};

export default RoutePhotosGallery;
